<template>
  <OurPage
    class="dealership-applications-details"
    :title="$t('title.dealershipApplications.details')"
    :back-route="backRoute"
    width="xxl"
    :loading="!detailsReady"
  >
    <template #header-right>
      <OurTag v-if="status" :text="status?.text" :color="status?.color" />
    </template>

    <ApplicationsForm
      ref="userForm"
      v-model="form"
      class="applications-form"
      edit-form
      :loading="loading"
      :sent-to-one-c-loading="sentToOneCLoading"
      :regions="regions"
      :status="status"
      :bonus-levels="bonusLevels"
      :is-shown-change-region-select="isShownChangeRegionSelect"
      :is-shown-change-manager-select="isShownChangeManagerSelect"
      :draft-region-id="draftRegionId"
      :draft-manager-id="draftManagerId"
      :view-only="isApplicationFormReadOnly"
      @sendToOneC="sendToOneC"
      @reload="reloadCompanies"
      @update-draft-region-id="draftRegionId = $event"
      @update-draft-manager-id="draftManagerId = $event"
      @changeRegion="changeRegion"
      @cancelChangeRegion="cancelChangeRegion"
      @changeManager="changeManager"
      @cancelChangeManager="cancelChangeManager"
      @tab-change="dealershipFormTab = $event"
    />

    <template #footer-left>
      <div class="flex flex-wrap items-center gap-2">
        <OurButton
          v-if="isDealershipButtonsVisible"
          class="button"
          :text="$t('button.confirm')"
          @click="onApproveButton"
        />

        <OurButton
          v-if="isDealershipRegionManagerWorkflowVisible"
          class="button"
          :text="$t('button.changeRegion')"
          @click="toggleChangeRegionSelect"
        />

        <OurButton
          v-if="isDealershipRegionManagerWorkflowVisible"
          class="button"
          :text="$t('button.changeManager')"
          @click="toggleChangeManagerSelect"
        />

        <OurButton
          v-if="!form.youcontrolSynced && isDealershipButtonsVisible"
          class="button"
          :text="$t('button.syncWith1C')"
          @click="onSynchronizeWith1C"
        />

        <OurButton class="button" :text="$t('button.back')" variant="secondary" @click="goToList" />
      </div>
    </template>

    <template #footer-right>
      <OurButton
        v-if="isDealershipButtonsVisible"
        class="button"
        variant="thirdary"
        :text="$t('button.reject')"
        @click="onRejected"
      />
    </template>
  </OurPage>
</template>

<script>
import { mapGetters, mapMutations } from "vuex";
import apolloMixin from "@/mixins/apollo.mixin";
import {
  accessGroupsQuery,
  bonusProgramLevelsQuery,
  customerApplicationQuery,
  newNoticesAddDataQuery,
  rolesQuery,
} from "./gql/queries";
import {
  updateCustomerApplicationMutation,
  changeCustomerApplicationManagerMutation,
  changeCustomerApplicationRegionMutation,
  recordCustomerApplicationOpenedMutation,
  sendCustomerApplicationToOneCMutation,
  syncCustomerApplicationWithOneCMutation,
} from "./gql/mutations";
import ApplicationsForm from "@/views/settings/dealership-applications/_components/ApplicationsForm.vue";
import { getCompanies } from "@/api/companies";
import {
  extractRegionManagerIds,
  extractStoreCompanies,
  mapCustomerApplicationToForm,
} from "./utils/mapCustomerApplicationToForm";

export default {
  name: "DealershipApplicationsDetails",

  components: {
    ApplicationsForm,
  },

  mixins: [apolloMixin],

  data() {
    return {
      isShownChangeRegionSelect: false,
      isShownChangeManagerSelect: false,
      detailsReady: false,
      backRoute: {
        name: "Back",
        title: this.$t("title.dealershipApplications._"),
      },
      companies: [],
      loading: false,
      sentToOneCLoading: false,
      regions: [],
      bonusLevels: [],
      status: "",
      initialRegionId: "",
      initialManagerId: "",
      draftRegionId: "",
      draftManagerId: "",
      dealershipFormTab: "addNew",
      form: {
        companyName: "",
        companyFullName: "",
        companyAccountingName: "",
        companyEmail: "",
        companyPhone: "",
        companyCode: "",
        companyType: "",
        legalAddress: "",
        youcontrolSynced: false,
        sentToOneC: false,
        verificationErrors: [],
        creationErrors: [],
        verifiedAt: null,
        name: "",
        taxNumber: "",
        lastName: "",
        firstName: "",
        roleId: "",
        region: "",
        managerId: "",
        processingManagerId: "",
        email: "",
        phone: "",
        comment: "",
        mainActivityDirection: "",
        mainClients: "",
        employeesTotal: "",
        employeesInstallers: "",
        employeesEngineers: "",
        employeesCommercial: "",
        companyYears: "",
        dealerBrands: "",
        trainingMountingService: "",
        trainingCalculationsDesign: "",
        trainingSalesPromotion: "",
        plannedAtmosferaVolumeUsd: "",
        employeesRenewableEnergy: "",
        storageId: "",
        accessGroupId: "",
        levelId: "1",
        companies: [],
        customerId: "",
        heardAboutSource: "",
        heardAboutSourceOther: "",
      },
    };
  },

  computed: {
    ...mapGetters("user", ["getPermission"]),
    ...mapGetters("dealershipApplications", ["getStatus"]),

    havePermissionEdit() {
      return this.getPermission["customer.applications"];
    },

    isViewOnlyApplication() {
      return this.$store.getters["dealershipApplications/isApplicationDetailsViewOnly"](
        this.status?.value
      );
    },

    isDealershipHistoryOrCommentsTab() {
      return this.dealershipFormTab === "history" || this.dealershipFormTab === "comments";
    },

    isDealershipAddOneMoreTab() {
      return this.dealershipFormTab === "add";
    },

    isApplicationFormReadOnly() {
      return !this.detailsReady || this.isViewOnlyApplication;
    },

    isDealershipDetailsFooterReady() {
      return (
        this.detailsReady &&
        !this.isDealershipHistoryOrCommentsTab &&
        !this.isShownChangeRegionSelect &&
        !this.isShownChangeManagerSelect
      );
    },

    isDealershipRegionManagerWorkflowVisible() {
      return (
        this.isDealershipDetailsFooterReady &&
        !this.isDealershipAddOneMoreTab &&
        !this.isViewOnlyApplication
      );
    },

    isDealershipButtonsVisible() {
      return (
        this.isDealershipDetailsFooterReady &&
        !this.isViewOnlyApplication &&
        this.havePermissionEdit
      );
    },
  },

  async created() {
    try {
      await Promise.all([this.getData(), this.getSupportingData()]);
    } finally {
      this.detailsReady = true;

      if (this.status?.value === "new_for_manager") {
        this.recordCustomerApplicationOpened();
      }
    }
  },

  methods: {
    ...mapMutations("dealerUsers", ["SET_ROLES", "SET_COMPANIES"]),
    ...mapMutations("dealerCompany", ["SET_ACCESS_GROUPS", "SET_MANAGERS"]),
    ...mapMutations("accessGroups", ["SET_STORAGES"]),

    async reloadCompanies() {
      this.loading = true;
      const allCompanies = await getCompanies("without");

      this.SET_COMPANIES([...allCompanies.data, ...this.companies]);
      this.loading = false;
    },

    async getData() {
      const [{ customerApplication }, allCompanies] = await Promise.all([
        this.$get(customerApplicationQuery, {
          id: this.$route.params.id,
        }),
        getCompanies("without"),
      ]);

      if (customerApplication) {
        this.applyCustomerApplication(customerApplication, allCompanies);
      }
    },

    applyCustomerApplication(customerApplication, allCompanies) {
      this.companies = extractStoreCompanies(customerApplication);
      this.status = this.getStatus(customerApplication.status);
      this.form = mapCustomerApplicationToForm(customerApplication, {
        levelId: this.form.levelId,
      });

      const { regionId, managerId } = extractRegionManagerIds(customerApplication);

      this.initialRegionId = regionId;
      this.initialManagerId = managerId;
      this.draftRegionId = regionId;
      this.draftManagerId = managerId;

      this.SET_COMPANIES([...allCompanies.data, ...this.companies]);
    },

    async getSupportingData() {
      const [
        { allManagers, regions, storages },
        { roles },
        { bonusProgramLevels },
        { accessGroups },
      ] = await Promise.all([
        this.$get(newNoticesAddDataQuery),
        this.$get(rolesQuery),
        this.$get(bonusProgramLevelsQuery),
        this.$get(accessGroupsQuery),
      ]);

      if (regions) {
        this.regions = regions.map((item) => {
          return {
            id: item.id,
            name: item.name?.ua,
          };
        });
      }

      if (storages) {
        this.SET_STORAGES(storages);
      }

      if (allManagers) {
        this.SET_MANAGERS(allManagers);
      }

      if (bonusProgramLevels) {
        this.bonusLevels = bonusProgramLevels.map((bonusLevelsSection) => {
          const { id, name } = bonusLevelsSection;

          return {
            id,
            name: name.toUpperCase(),
          };
        });
      }

      if (!this.form.accessGroupId) {
        this.form.accessGroupId = accessGroups?.[0]?.id;
      }

      this.SET_ACCESS_GROUPS(accessGroups);
      this.SET_ROLES(roles);
    },

    async updateMutation(status) {
      const payload = {
        id: this.$route.params.id,
        status: status,
        comment: this.form.comment,
        companyName: this.form.companyName,
        regionId: this.form.regionId,
        dealer: {
          roleId: this.form.roleId,
        },
        managerId: this.form.managerId,
        processingManagerId: this.form.processingManagerId,
      };

      if (!this.form.customerId) {
        payload.customer = {
          name: this.form.name,
          companies: this.form.companies?.map((company) => company?.id || company) ?? [],
          storageId: this.form.storageId,
          accessGroupId: this.form.accessGroupId,
          levelId: this.form.levelId,
        };
      } else {
        payload.customerId = this.form.customerId;
      }

      const { updateCustomerApplication } = await this.$post(
        updateCustomerApplicationMutation,
        payload,
        {
          delaySuccessNotify: true,
        }
      );

      if (updateCustomerApplication.id) this.goToList();
    },

    async onApproveButton() {
      this.$refs.userForm.emitForm();

      if (this.form.isValid) {
        await this.updateMutation("approved");
      }
    },

    async onSynchronizeWith1C() {
      if (!this.$refs.userForm.emitSyncWith1CForm()) {
        return;
      }

      const { syncCustomerApplicationWithOneC } = await this.$post(
        syncCustomerApplicationWithOneCMutation,
        {
          id: this.$route.params.id,
          companyName: this.form.companyName,
          ...(this.form.companyType === "juridicalPerson" && {
            taxNumber: this.form.taxNumber,
          }),
          companyCode: this.form.companyCode,
          firstName: this.form.firstName,
          lastName: this.form.lastName,
          phone: this.form.phone,
          email: this.form.email,
        },
        {
          withNotify: true,
        }
      );

      if (syncCustomerApplicationWithOneC) {
        const allCompanies = await getCompanies("without");

        this.applyCustomerApplication(syncCustomerApplicationWithOneC, allCompanies);

        if (this.form.verificationErrors?.length) {
          this.$refs.userForm.scrollToVerificationErrors();
        }
      }
    },

    async sendToOneC(data) {
      this.sentToOneCLoading = true;

      try {
        const { sendCustomerApplicationToOneC } = await this.$post(
          sendCustomerApplicationToOneCMutation,
          {
            id: this.$route.params.id,
            companyName: data.companyName,
            regionId: data.regionId,
            managerId: data.managerId,
            processingManagerId: data.processingManagerId,
            ...(this.form.companyType === "juridicalPerson" && {
              taxNumber: data.taxNumber,
            }),
            firstName: data.firstName,
            lastName: data.lastName,
            phone: data.phone,
            email: data.email,
            companyCode: data.companyCode,
          },
          {
            withNotify: true,
          }
        );

        if (sendCustomerApplicationToOneC) {
          const allCompanies = await getCompanies("without");

          this.applyCustomerApplication(sendCustomerApplicationToOneC, allCompanies);
        }
      } finally {
        this.sentToOneCLoading = false;
      }
    },

    async onRejected() {
      this.$refs.userForm.emitSaveForm();

      await this.updateMutation("rejected");
    },

    toggleChangeRegionSelect() {
      this.isShownChangeRegionSelect = !this.isShownChangeRegionSelect;

      if (this.isShownChangeRegionSelect) {
        this.$refs.userForm.scrollToRegionSelect();
        this.draftRegionId = this.form.regionId;
        this.isShownChangeManagerSelect = false;
      }
    },

    toggleChangeManagerSelect() {
      this.isShownChangeManagerSelect = !this.isShownChangeManagerSelect;

      if (this.isShownChangeManagerSelect) {
        this.$refs.userForm.scrollToManagerSelect();
        this.draftManagerId = this.form.managerId;
        this.isShownChangeRegionSelect = false;
      }
    },

    cancelChangeRegion() {
      this.draftRegionId = this.initialRegionId;
      this.isShownChangeRegionSelect = false;
    },

    cancelChangeManager() {
      this.draftManagerId = this.initialManagerId;
      this.isShownChangeManagerSelect = false;
    },

    async changeRegion() {
      if (!this.$refs.userForm.emitRegionChangeForm()) {
        return;
      }

      await this.$post(changeCustomerApplicationRegionMutation, {
        id: this.$route.params.id,
        regionId: this.form.regionId,
      });
      this.initialRegionId = this.form.regionId;
      this.draftRegionId = this.form.regionId;
      this.isShownChangeRegionSelect = false;
      this.form.isValid = false;
      this.goToList();
    },

    async changeManager() {
      if (this.isViewOnlyApplication) {
        return;
      }

      if (!this.$refs.userForm.emitManagerChangeForm()) {
        return;
      }

      await this.$post(changeCustomerApplicationManagerMutation, {
        id: this.$route.params.id,
        managerId: this.form.managerId,
      });
      this.initialManagerId = this.form.managerId;
      this.draftManagerId = this.form.managerId;
      this.isShownChangeManagerSelect = false;
      this.form.isValid = false;
      this.goToList();
    },

    async recordCustomerApplicationOpened() {
      await this.$post(
        recordCustomerApplicationOpenedMutation,
        {
          id: this.$route.params.id,
        },
        {
          withNotify: false,
        }
      );
    },

    goToList() {
      this.$router.back();
    },
  },
};
</script>

<style lang="postcss" scoped>
.dealership-applications-details {
  @apply relative;

  .applications-form {
    @apply mb-6;
  }
}
</style>
